import { useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";

import "react-quill/dist/quill.snow.css";
import "quill-image-uploader/dist/quill.imageUploader.min.css";

import ImageResize from "@taoqf/quill-image-resize-module";
import { ImageActions } from "@xeger/quill-image-actions";
import { ImageFormats } from "@xeger/quill-image-formats";
import ImageUploader from "quill-image-uploader";

import { FormField } from "../FormField";
import { RichTextEditorProps } from "./RichTextEditor.types";

import styles from "./RichTextEditor.module.scss";

// Full-featured Quill article editor (FAQ). Emits and consumes HTML.
// Images upload through the app's direct-to-S3 flow and support resize,
// float and alt text (imageActions/imageFormats/imageResize modules).
// Videos are embedded by URL (Quill's stock iframe embed).
Quill.register("modules/imageUploader", ImageUploader);
Quill.register("modules/imageActions", ImageActions);
Quill.register("modules/imageFormats", ImageFormats);
Quill.register("modules/imageResize", ImageResize);

const ImageFormat = Quill.import("formats/image");

// Persist the `alt` attribute on images (set via the custom "alt" toolbar
// button) so the app can read it.
class ImageWithAlt extends ImageFormat {
  static formats(domNode: HTMLElement) {
    const alt = domNode.getAttribute("alt");
    return { alt: alt || "" };
  }

  format(name: string, value: string) {
    if (name === "alt") {
      this.domNode.setAttribute("alt", value);
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(ImageWithAlt, true);

const FORMATS = [
  "bold",
  "italic",
  "underline",
  "strike",
  "align",
  "list",
  "indent",
  "size",
  "header",
  "link",
  "image",
  "video",
  "color",
  "background",
  "clean",
  "imageBlot",
  "float",
  "width",
  "height",
  "alt"
];

const RichTextEditor = ({
  defaultValue,
  onChange,
  onUploadImage,
  ...formFieldProps
}: RichTextEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);

  const onUploadImageRef = useRef(onUploadImage);
  onUploadImageRef.current = onUploadImage;

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ color: [] }, { background: [] }],
          [{ size: ["small", false, "large", "huge"] }],
          ["link", "image", "video"],
          ["clean"],
          ["alt"]
        ],
        handlers: {
          alt: () => {
            const editor = quillRef.current?.getEditor();
            const range = editor?.getSelection();

            if (!editor || !range) {
              alert("No selection. Please select an image to add alt text.");
              return;
            }

            const [leaf] = editor.getLeaf(range.index);
            const domNode = (leaf as { domNode?: HTMLElement })?.domNode;
            if (domNode?.tagName === "IMG") {
              const currentAlt = domNode.getAttribute("alt") || "";
              const newAlt = prompt("Enter alt text for the image:", currentAlt);
              if (newAlt !== null) {
                domNode.setAttribute("alt", newAlt);
              }
            } else {
              alert("Please select an image to add alt text.");
            }
          }
        }
      },
      imageActions: {},
      imageFormats: {},
      imageResize: {
        modules: ["Resize", "DisplaySize", "Toolbar"]
      },
      imageUploader: {
        upload: (file: File) => onUploadImageRef.current(file)
      },
      clipboard: {
        matchVisual: false
      }
    }),
    []
  );

  return (
    <FormField containerClassName={styles.container} {...formFieldProps}>
      <ReactQuill
        ref={quillRef}
        defaultValue={defaultValue ?? ""}
        onChange={onChange}
        modules={modules}
        formats={FORMATS}
        className={styles.quill}
      />
    </FormField>
  );
};

export default RichTextEditor;
